import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { changePassword } from '../../../../controllers/AuthController';
import { toast } from 'react-hot-toast';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ButtonPrimary from '../../../../components/button/ButtonPrimary';

const Card = ({ t }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
    const navigate = useNavigate();
    
    const toggleShowCurrentPassword = () => {
        setShowCurrentPassword((prev) => !prev);
    };
    
    const toggleShowNewPassword = () => {
        setShowNewPassword((prev) => !prev);
    };
    
    const toggleShowConfirmNewPassword = () => {
        setShowConfirmNewPassword((prev) => !prev);
    };
    
    const isDisabled = useMemo(() => {
        return !(currentPassword.length >= 8 && newPassword.length >= 8 && confirmNewPassword.length >= 8 && newPassword === confirmNewPassword);
    }, [currentPassword, newPassword, confirmNewPassword]);
    
    const handleChangePassword = async () => {
        try {
            await changePassword({
                current_password: currentPassword,
                new_password: newPassword,
                confirm_new_password: confirmNewPassword
            });
            
            navigate('/dashboard');
            toast.success(t('changePassword.successMessage'));
        } catch (error) {
            toast.error(error.message);
        };
    };
    
    return (
        <div className='flex flex-col p-5 rounded-lg bg-white space-y-5'>
            {/* Current Password */}
            <div className='relative'>
                <div className='flex flex-grow items-center'>
                    <label className='w-36 lg:w-64 text-xs md:text-sm'>{t('changePassword.currentPassword.title')}:</label>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type={showCurrentPassword ? 'text' : 'password'}
                        placeholder={t('changePassword.currentPassword.placeholderText')}
                        required
                        value={currentPassword}
                        onChange={(e) => {
                            const input = e.target.value.replace(/\s/g, '');
                            setCurrentPassword(input);
                        }} 
                    />
                    <button
                        className='absolute flex right-3 text-grayX11'
                        type='button'
                        aria-label={showCurrentPassword ? t('common.password.ariaLabelHide') : t('common.password.ariaLabelShow')}
                        onClick={toggleShowCurrentPassword}
                    >
                        {showCurrentPassword ? <VisibilityOffIcon sx={{ fontSize: '1.25rem' }} /> : <VisibilityIcon sx={{ fontSize: '1.25rem' }} />}
                    </button>
                </div>
            </div>
            
            {/* New Password */}
            <div className='relative'>
                <div className='flex flex-grow items-center'>
                    <label className='w-36 lg:w-64 text-xs md:text-sm'>{t('changePassword.newPassword.title')}:</label>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder={t('changePassword.newPassword.placeholderText')}
                        required
                        value={newPassword}
                        onChange={(e) => {
                            const input = e.target.value.replace(/\s/g, '');
                            setNewPassword(input);
                        }} 
                    />
                    <button
                        className='absolute flex right-3 text-grayX11'
                        type='button'
                        aria-label={showNewPassword ? t('common.password.ariaLabelHide') : t('common.password.ariaLabelShow')}
                        onClick={toggleShowNewPassword}
                    >
                        {showNewPassword ? <VisibilityOffIcon sx={{ fontSize: '1.25rem' }} /> : <VisibilityIcon sx={{ fontSize: '1.25rem' }} />}
                    </button>
                </div>
            </div>
            
            {/* Confirm New Password */}
            <div className='relative'>
                <div className='flex flex-grow items-center'>
                    <label className='w-36 lg:w-64 text-xs md:text-sm'>{t('changePassword.confirmPassword.title')}:</label>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type={showConfirmNewPassword ? 'text' : 'password'}
                        placeholder={t('changePassword.confirmPassword.placeholderText')}
                        required
                        value={confirmNewPassword}
                        onChange={(e) => {
                            const input = e.target.value.replace(/\s/g, '');
                            setConfirmNewPassword(input);
                        }} 
                    />
                    <button
                        className='absolute flex right-3 text-grayX11'
                        type='button'
                        aria-label={showConfirmNewPassword ? t('common.password.ariaLabelHide') : t('common.password.ariaLabelShow')}
                        onClick={toggleShowConfirmNewPassword}
                    >
                        {showConfirmNewPassword ? <VisibilityOffIcon sx={{ fontSize: '1.25rem' }} /> : <VisibilityIcon sx={{ fontSize: '1.25rem' }} />}
                    </button>
                </div>
            </div>
            
            {/* Button */}
            <div className='flex justify-center sm:justify-end'>
                <ButtonPrimary className='h-full w-36 md:w-64 md:text-sm' isDisabled={isDisabled} onClick={handleChangePassword}>{t('changePassword.button')}</ButtonPrimary>
            </div>
        </div>
    );
};

export default Card;
